/**
 * `call` — 1v1 suhbat. docs/SPEC.md §call, faza 1.
 *
 * Prototipdan IKKI CHETLANISH:
 *   · `★ 4.9` reytingi YO'Q — mahsulotda reyting mexanikasi mavjud emas
 *   · `Toshkent` shahri YO'Q — joylashuv yig'ilmaydi (D-018 da faqat
 *     yosh va jins so'raladi)
 *   Bo'lmagan ma'lumotni ko'rsatish prototipga sodiqlik emas, yolg'on.
 *
 * **Anonim rejim serverda kesiladi** (D-035 emas, `match.found` izohi):
 * ism va rasm `null` bo'lib keladi, klient ularni yashirmaydi. Ochilish
 * `reveal_after_seconds` dan keyin — hozircha taymer bilan, keyingi
 * blokda server eventi bilan almashadi.
 */

import type { GiftOut, MatchFound, WsEvent } from '@voice/api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { setClosingConfirmation } from '../app/telegram';
import { uz } from '../i18n/uz';
import { useMe } from '../lib/api/useMe';
import { useConfig } from '../lib/config/useConfig';
import { duration as formatDuration } from '../lib/format';
import { useSendGift } from '../lib/api/useGifts';
import { useSendFriendRequest } from '../lib/api/useSocial';
import { useRealtimeEvents } from '../lib/realtime/context';
import { useVoiceCall } from '../lib/rtc/useVoiceCall';
import { GiftFx, type GiftFxPayload } from '../ui/GiftFx';
import { GiftSheet } from '../ui/GiftSheet';
import { Icon, Toast } from '../ui';
import { useToast } from '../ui/useToast';
import { paletteVars } from '@voice/ui-tokens';
import './screens.css';

export function Call() {
  const navigate = useNavigate();
  const location = useLocation();
  const me = useMe();
  const config = useConfig();
  const toast = useToast();

  const match = (location.state as { match?: MatchFound } | null)?.match;
  const [seconds, setSeconds] = useState(0);

  // `match` bo'lmasa bu ekranga to'g'ridan-to'g'ri kelingan (yangilash,
  // havola). Qidiruvga qaytaramiz — bu yerda ko'rsatadigan narsa yo'q.
  useEffect(() => {
    if (!match) navigate('/match', { replace: true });
  }, [match, navigate]);

  // Tasodifan yopib suhbatdan chiqib ketmasin (docs/TELEGRAM.md).
  useEffect(() => {
    setClosingConfirmation(true);
    return () => setClosingConfirmation(false);
  }, []);

  // Taymer SERVERDAGI `started_at` dan sanaladi, nol'dan emas.
  //
  // Mahalliy hisob ekran ochilgan paytga bog'liq bo'lardi: komponent
  // qayta o'rnatilsa taymer nol'dan boshlanardi va ikki tomonda ikki xil
  // raqam chiqardi. Qabul mezoni esa aynan "taymer TO'G'RI" deydi.
  useEffect(() => {
    if (!match) return;
    const startedAt = new Date(match.started_at).getTime();
    const tick = () => setSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1_000)));
    tick();
    const timer = setInterval(tick, 1_000);
    return () => clearInterval(timer);
  }, [match]);

  if (!match || !me.data) {
    return (
      <div className="screen">
        <div className="empty">{uz.common.loading}</div>
      </div>
    );
  }

  return (
    <CallBody
      match={match}
      myUserId={me.data.id}
      myPalette={me.data.palette_idx}
      seconds={seconds}
      stunUrls={(config.data?.rtc['stun_urls'] as string[] | undefined) ?? []}
      p2pTimeoutMs={Number(config.data?.rtc['p2p_timeout_ms'] ?? 6_000)}
      gifts={config.data?.catalog.gifts ?? []}
      giftsEnabled={config.data?.flags['gifts_enabled'] ?? false}
      confirmThreshold={Number(config.data?.limits['gift_confirm_threshold'] ?? 10_000)}
      balance={me.data.gems}
      onFinished={() => navigate('/match', { replace: true })}
      toast={toast}
    />
  );
}

function CallBody({
  match,
  myUserId,
  myPalette,
  seconds,
  stunUrls,
  p2pTimeoutMs,
  gifts,
  giftsEnabled,
  confirmThreshold,
  balance,
  onFinished,
  toast,
}: {
  match: MatchFound;
  myUserId: number;
  myPalette: number;
  seconds: number;
  stunUrls: string[];
  p2pTimeoutMs: number;
  gifts: GiftOut[];
  giftsEnabled: boolean;
  confirmThreshold: number;
  balance: number;
  onFinished: () => void;
  toast: { message: string | null; show: (text: string) => void };
}) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const sendGift = useSendGift();
  const sendFriendRequest = useSendFriendRequest();
  const [sheetOpen, setSheetOpen] = useState(false);
  /* So'rov ketdimi. Ikkinchi bosish serverdan `FRIENDSHIP_EXISTS`
     olardi — bu xato emas, lekin toast bo'lib chiqsa xatoga o'xshaydi. */
  const [friendSent, setFriendSent] = useState(false);
  // Dinamik. Audio chiqish qurilmasini almashtirish Telegram
  // WebView'da yo'q (`setSinkId` qo'llab-quvvatlanmaydi), shuning
  // uchun bu suhbatdosh ovozini o'chiradi — `<audio muted>`.
  const [speakerOn, setSpeakerOn] = useState(true);
  const [fx, setFx] = useState<GiftFxPayload | null>(null);

  // Suhbatdosh sovg'a yuborsa FX shu yerdan keladi.
  useRealtimeEvents(
    useCallback(
      (event: WsEvent) => {
        if (event.type === 'gift.received' && event.match_id === match.match_id) {
          setFx({ icon: event.icon, name: event.gift_name, combo: event.combo });
        }
      },
      [match.match_id],
    ),
  );

  async function send(giftId: number, combo: 1 | 10 | 99): Promise<void> {
    const gift = gifts.find((g) => g.id === giftId);
    setSheetOpen(false);
    try {
      await sendGift.mutateAsync({
        gift_id: giftId,
        receiver_id: match.peer.id,
        combo,
        context: 'call',
        match_id: match.match_id,
        client_tx_id: crypto.randomUUID(),
      });
      // Yuboruvchi FX'ni DARHOL ko'radi — serverdan qaytishini kutmaydi.
      if (gift) setFx({ icon: gift.icon, name: gift.name, combo });
    } catch (err) {
      const code = (err as { code?: string }).code;
      toast.show(code === 'INSUFFICIENT_FUNDS' ? uz.gift.notEnough : uz.common.error);
    }
  }

  /**
   * Do'stlikka chorlash — SPEC §call 201 dagi beshinchi tugma.
   *
   * Anonim suhbatda ham ochiq: suhbatdosh MEN uchun yopiq, men esa
   * so'rovni O'ZIM yuboraman. Ya'ni bu ochilish emas, ixtiyoriy
   * tanishuv — 1v1 dan keyin aloqada qolishning yagona yo'li
   * (`chats` dagi ID bo'yicha qo'shish olib tashlangan).
   */
  async function inviteToFriends(): Promise<void> {
    try {
      await sendFriendRequest.mutateAsync(match.peer.id);
      setFriendSent(true);
      toast.show(uz.call.friendSent);
    } catch (err) {
      // Server matni aniq: "allaqachon do'stsiz", "so'rov yuborilgan".
      toast.show(err instanceof Error ? err.message : uz.common.error);
    }
  }

  const call = useVoiceCall({
    matchId: match.match_id,
    myUserId,
    peerUserId: match.peer.id,
    stunUrls,
    p2pTimeoutMs,
    onPeerLeft: () => {
      toast.show(uz.call.peerLeft);
      onFinished();
    },
  });

  // Suhbatdoshning ovozi. `<audio>` DOM'da turadi va manba o'zgarganda
  // qayta ulanadi — React `srcObject` ni o'zi boshqara olmaydi.
  useEffect(() => {
    if (audio.current && call.remoteStream) {
      audio.current.srcObject = call.remoteStream;
      void audio.current.play().catch(() => undefined);
    }
  }, [call.remoteStream]);

  const anonymous = match.anonymous;
  const peerName = anonymous ? uz.call.anonymous : (match.peer.first_name ?? uz.call.anonymous);
  const displayName = match.peer.age ? `${peerName}, ${match.peer.age}` : peerName;

  async function hangUp(): Promise<void> {
    await call.hangUp();
    onFinished();
  }

  return (
    <div className="call">
      {toast.message && <Toast message={toast.message} />}
      {/* Suhbatdoshning ovozi. Ko'rinmaydi — `<audio>` faqat chiqarish
          uchun, boshqaruv elementlari qo'ng'iroq tugmalarida. */}
      <audio ref={audio} autoPlay muted={!speakerOn} />

      <div className="call__label">{uz.call.encrypted}</div>
      <div className="call__timer tnum">{formatDuration(seconds)}</div>

      <div className="call__body">
        <div className="call__avatar">
          {call.state === 'connected' && <div className="call__ring" />}
          <div
            className="call__face"
            style={paletteVars(anonymous ? myPalette : match.peer.palette_idx)}
          >
            {anonymous ? '?' : peerName.slice(0, 1)}
          </div>
          {/* Anonim rejimda level ham yashiriladi: u kimligini toraytiruvchi
              ma'lumot va serverdan `peer.level` anonimda kelmaydi. */}
          {!anonymous && match.peer.level !== null && (
            <div className="call__level">{`${uz.profile.level} ${match.peer.level}`}</div>
          )}
        </div>

        <div className="call__name">{displayName}</div>

        <div className="call__tags">
          {match.peer.lang && <div className="call__tag">{uz.langName[match.peer.lang]}</div>}
          {match.peer.gender && (
            <div className="call__tag">
              {match.peer.gender === 'female' ? uz.match.female : uz.match.male}
            </div>
          )}
          {anonymous && (
            <div className="call__tag">
              {uz.call.revealIn(String(Math.round(match.reveal_after_seconds / 60)))}
            </div>
          )}
        </div>

        {call.state === 'connected' && <Wave />}

        <div className="call__status">{uz.call.state[call.state]}</div>

        <button
          type="button"
          className="call__gift-pill"
          onClick={() => (giftsEnabled ? setSheetOpen(true) : toast.show(uz.settings.notYet))}
        >
          <Icon name="gift" size={15} color="var(--gold)" />
          <span>{uz.call.gift}</span>
        </button>
      </div>

      <div className="call__controls">
        <button
          type="button"
          className={`call-btn ${call.muted ? 'call-btn--muted' : 'call-btn--mic-on'}`}
          onClick={call.toggleMute}
          aria-label={call.muted ? uz.call.unmute : uz.call.mute}
        >
          <Icon name={call.muted ? 'micoff' : 'mic'} size={21} />
        </button>

        <button
          type="button"
          className={`call-btn${speakerOn ? '' : ' call-btn--off'}`}
          onClick={() => setSpeakerOn((on) => !on)}
          aria-label={speakerOn ? uz.call.speakerOff : uz.call.speakerOn}
        >
          <Icon name="speaker" size={21} color={speakerOn ? 'var(--fg-80)' : 'var(--fg-35)'} />
        </button>

        <button
          type="button"
          className="call-btn call-btn--hangup"
          onClick={() => void hangUp()}
          aria-label={uz.call.hangUp}
        >
          <Icon name="call" size={26} style={{ transform: 'rotate(134deg)' }} />
        </button>

        <button
          type="button"
          className={friendSent ? 'call-btn call-btn--off' : 'call-btn'}
          onClick={() => void inviteToFriends()}
          disabled={friendSent || sendFriendRequest.isPending}
          aria-label={uz.call.addFriend}
        >
          {/* Rang SINF bilan emas, prop bilan: `Icon` ga berilgan rang
              tugmadagi `color` ni bosib ketadi, ya'ni `call-btn--off`
              o'zi ikonkani so'ndira olmaydi. */}
          <Icon name="adduser" size={21} color={friendSent ? 'var(--fg-35)' : 'var(--fg-80)'} />
        </button>

        <button
          type="button"
          className="call-btn call-btn--gift"
          // Flag o'chiq bo'lsa tugma jim ishlamay qolmasin (qoida 23).
          onClick={() => (giftsEnabled ? setSheetOpen(true) : toast.show(uz.settings.notYet))}
          aria-label={uz.call.gift}
        >
          <Icon name="gift" size={21} />
        </button>
      </div>

      {sheetOpen && (
        <GiftSheet
          gifts={gifts}
          balance={balance}
          confirmThreshold={confirmThreshold}
          onSend={(giftId, combo) => void send(giftId, combo)}
          onClose={() => setSheetOpen(false)}
        />
      )}

      {fx && <GiftFx gift={fx} onDone={() => setFx(null)} />}
    </div>
  );
}

/* ── Ovoz to'lqini ────────────────────────────────────────────────────
 * Prototip 650-656. Bu VIZUALIZATSIYA emas — mikrofon signaliga
 * bog'lanmagan. Sabab: haqiqiy analiz uchun `AudioContext` va har kadrda
 * hisob kerak, bu esa batareyani yeydi va faza 1 da hech narsa bermaydi.
 * Chiziqlar shunchaki "efir ketmoqda" degan belgi.
 *
 * Balandliklar prototipdagi kabi tasodifiy emas, QAT'IY ro'yxat: har
 * render'da o'zgarsa chiziqlar sakrab ketardi. */
const WAVE = [14, 26, 9, 33, 18, 30, 11, 36, 22, 28, 13, 31, 17, 24, 10, 20];

function Wave() {
  return (
    <div className="call__wave" aria-hidden="true">
      {WAVE.map((height, i) => (
        <div
          key={i}
          className="call__wave-bar"
          /* .15s qadam — DESIGN_TOKENS §11. `.18` hech qaysi manbada yo'q. */
          style={{ height, animationDelay: `${(i % 5) * 0.15}s` }}
        />
      ))}
    </div>
  );
}
